import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { FloatingBadge } from "../../types/landing";
import { uploadService } from "../../services/uploadService";
import { toast } from "react-hot-toast";

interface FloatingBadgeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  badge: FloatingBadge | null;
  mode: "create" | "edit";
}

// Formats an ISO date string for an <input type="date"> value.
const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const FloatingBadgeFormModal: React.FC<FloatingBadgeFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  badge,
  mode,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: {
      url: "",
      alt: "",
    },
    actionType: "video" as "video" | "link" | "none",
    videoUrl: "",
    link: "",
    position: "bottom-right" as
      | "bottom-right"
      | "bottom-left"
      | "top-right"
      | "top-left",
    status: "active" as "active" | "inactive",
    startDate: "",
    endDate: "",
    displayOrder: 0,
  });

  const [imagePreview, setImagePreview] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(
    null
  );
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (badge && mode === "edit") {
      setFormData({
        title: badge.title || "",
        description: badge.description || "",
        image: {
          url: badge.image?.url || "",
          alt: badge.image?.alt || "",
        },
        actionType: badge.actionType || "video",
        videoUrl: badge.videoUrl || "",
        link: badge.link || "",
        position: badge.position || "bottom-right",
        status: badge.status || "active",
        startDate: toDateInputValue(badge.startDate),
        endDate: toDateInputValue(badge.endDate),
        displayOrder: badge.displayOrder || 0,
      });
      setImagePreview(badge.image?.url || "");
    } else {
      resetForm();
    }
  }, [badge, mode]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: {
        url: "",
        alt: "",
      },
      actionType: "video",
      videoUrl: "",
      link: "",
      position: "bottom-right",
      status: "active",
      startDate: "",
      endDate: "",
      displayOrder: 0,
    });
    setImagePreview("");
    setSelectedImageFile(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("image.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        image: {
          ...prev.image,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        e.target.value = ""; // Reset input
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        e.target.value = ""; // Reset input
        return;
      }

      setSelectedImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.actionType === "video" && !formData.videoUrl.trim()) {
      toast.error("Video URL is required when action type is Video");
      return;
    }
    if (formData.actionType === "link" && !formData.link.trim()) {
      toast.error("Link is required when action type is Link");
      return;
    }

    setUploading(true);

    try {
      let imageUrl = formData.image.url;

      // Upload image if a new file was selected
      if (selectedImageFile) {
        const uploadedUrl = await uploadService.uploadImage(
          selectedImageFile,
          "landingpage/floating-badges"
        );
        imageUrl = uploadedUrl;
      }

      if (!imageUrl) {
        toast.error("Please select an image for the badge");
        setUploading(false);
        return;
      }

      const submitData = {
        ...formData,
        image: {
          url: imageUrl,
          alt: formData.image.alt || formData.title,
        },
        videoUrl: formData.actionType === "video" ? formData.videoUrl : "",
        link: formData.actionType === "link" ? formData.link : "",
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        displayOrder: Number(formData.displayOrder) || 0,
      };

      await onSubmit(submitData);
      resetForm();
    } catch (error) {
      console.error("Error submitting floating badge:", error);
      toast.error("Failed to save floating badge");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "create" ? "Add New Floating Badge" : "Edit Floating Badge"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. 7 Year Anniversary"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description about the badge"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Badge Image <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {imagePreview && (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-32 w-32 object-cover rounded-full border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview("");
                      setSelectedImageFile(null);
                      setFormData((prev) => ({
                        ...prev,
                        image: { ...prev.image, url: "" },
                      }));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition">
                  <Upload size={18} />
                  <span>Select Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                {selectedImageFile && (
                  <span className="text-sm text-gray-600">
                    {selectedImageFile.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                A round badge image will be uploaded when you save
              </p>
              <input
                type="text"
                name="image.alt"
                value={formData.image.alt}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Image alt text (optional)"
              />
            </div>
          </div>

          {/* Action Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action Type <span className="text-red-500">*</span>
            </label>
            <select
              name="actionType"
              value={formData.actionType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="video">Video (opens a floating player)</option>
              <option value="link">Link (opens in a new tab)</option>
              <option value="none">None (image only)</option>
            </select>
          </div>

          {/* Video URL */}
          {formData.actionType === "video" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Video URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
          )}

          {/* Link */}
          {formData.actionType === "link" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>
          )}

          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position
            </label>
            <select
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="top-right">Top Right</option>
              <option value="top-left">Top Left</option>
            </select>
          </div>

          {/* Scheduling window */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date (Optional)
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date (Optional)
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-4">
            Leave blank to show the badge indefinitely while active.
          </p>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
            <p className="text-sm text-gray-500 mt-1">
              Lower numbers appear first within the same position
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading}
            >
              {mode === "create" ? "Create Badge" : "Update Badge"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FloatingBadgeFormModal;
